import { AlertOctagon, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";

type AlertVariant = "default" | "warning" | "destructive" | "success";

interface AlertProps {
  variant?: AlertVariant;
  microcopies: {
    title: string;
    description?: string;
  };
}

const variantStyles: Record<
  AlertVariant,
  { container: string; text: string; icon: ReactNode }
> = {
  default: {
    container: "border-zinc-200",
    text: "text-zinc-900",
    icon: <CheckCircle2 className="shrink-0 text-zinc-900" size={16} />,
  },
  warning: {
    container: "border-orange-500",
    text: "text-orange-500",
    icon: <AlertTriangle className="shrink-0 text-orange-500" size={16} />,
  },
  destructive: {
    container: "border-red-600",
    text: "text-red-600",
    icon: <AlertOctagon className="shrink-0 text-red-600" size={16} />,
  },
  success: {
    container: "border-lime-500",
    text: "text-lime-700",
    icon: <CheckCircle2 className="shrink-0 text-lime-500" size={16} />,
  },
};

export function Alert(props: Readonly<AlertProps>): ReactNode {
  const variant = props.variant ?? "default";
  const styles = variantStyles[variant];

  return (
    <div
      className={`flex w-full flex-row gap-1 rounded-lg border bg-white px-4 py-3 ${styles.container}`}
      role="alert"
    >
      {styles.icon}
      <div className="flex flex-col">
        <span className={`font-semibold text-sm leading-4 ${styles.text}`}>
          {props.microcopies.title}
        </span>
        {props.microcopies.description && (
          <span className={`font-normal text-sm leading-4 ${styles.text}`}>
            {props.microcopies.description}
          </span>
        )}
      </div>
    </div>
  );
}
